import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {
    Box, Chip, LinearProgress, MenuItem, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField,
} from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tableStyles, tone } from '../../../utils/uiTokens'
import { money, toConfig } from '../../../utils/salaryFormulas'
import { salaryApiSlice, MentorSalaryRow } from '../../../features/salary/salarySlice'
import SalaryFactorRules from './components/SalaryFactorRules'
import { Content, Header, KpiRow, Toolbar, Wrapper } from './styles'
import ApprovalBadge from './components/ApprovalBadge'
import { useHeadScope } from '../../head/headScope'

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9]

const MentorSalaryPage: React.FC = () => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    // пән басшы видит этот же список по своим менторам и открывает их страницы у себя
    const { readOnly } = useHeadScope()

    const [cycleNumber, setCycleNumber] = useState<number>(1)
    // Фильтр по предмету ведём по НАЗВАНИЮ: один предмет существует как несколько
    // course_subject (по курсам/потокам), но название глобальное - дедуп по нему даёт 10 предметов
    const [subjectTitle, setSubjectTitle] = useState<string>('')
    const [groupId, setGroupId] = useState<number>(0)
    const [search, setSearch] = useState<string>('')

    // Грузим весь список цикла, предмет/группу/поиск фильтруем на клиенте,
    // чтобы в дропдаунах не было дублей и KPI совпадали с видимыми строками
    const { data, isFetching } = salaryApiSlice.useGetMentorSalaryListQuery({
        cycleNumber,
        courseSubjectId: 0,
        groupId: 0,
        filter: '',
    })
    const { data: configData } = salaryApiSlice.useGetSalaryConfigQuery()
    const config = useMemo(() => toConfig(configData?.entries), [configData])

    const subjectOf = (m: MentorSalaryRow) => m.subjectTitleKz || m.subjectTitleRu

    const mentorName = (m: MentorSalaryRow) =>
        `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || `#${m.mentorUserId}`

    // 10 уникальных предметов по названию
    const subjects = useMemo(() => {
        const seen = new Set<string>()
        const out: string[] = []
        data?.mentors?.forEach((m) => {
            const t = subjectOf(m)
            if (t && !seen.has(t)) { seen.add(t); out.push(t) }
        })
        return out
    }, [data])

    // Строки выбранного предмета (по ним строим список групп)
    const subjectRows = useMemo(
        () => (subjectTitle ? (data?.mentors ?? []).filter((m) => subjectOf(m) === subjectTitle) : (data?.mentors ?? [])),
        [data, subjectTitle],
    )

    const groups = useMemo(() => {
        const map = new Map<number, string>()
        subjectRows.forEach((m) => { if (m.groupId) map.set(m.groupId, m.groupTitle) })
        return Array.from(map.entries())
            .map(([id, title]) => ({ id, title }))
            .sort((a, b) => a.title.localeCompare(b.title))
    }, [subjectRows])

    // Если выбранная группа не входит в предмет - сбрасываем, иначе список опустеет
    const effectiveGroupId = groups.some((g) => g.id === groupId) ? groupId : 0

    const visibleRows = useMemo(
        () => subjectRows.filter((m) =>
            (!effectiveGroupId || m.groupId === effectiveGroupId) &&
            (!search || mentorName(m).toLowerCase().includes(search.toLowerCase())),
        ),
        [subjectRows, effectiveGroupId, search],
    )

    // KPI считаем по видимым строкам той же формулой, что и сервер
    const totalPayable = visibleRows.reduce((s, m) => s + (m.total || 0), 0)
    const mentorCount = visibleRows.length
    const averageSalary = mentorCount ? Math.round(totalPayable / mentorCount) : 0
    const filledCount = visibleRows.filter((m) => m.surveyFilled).length

    const isClosed = visibleRows.length > 0 && visibleRows.every((m) => m.closed)

    const openDetail = (row: MentorSalaryRow) => {
        const group = row.groupTitle ? `&group=${encodeURIComponent(row.groupTitle)}` : ''
        navigate(`${readOnly ? '/head/salary' : '/mentor-salary'}/${row.mentorUserId}?courseSubjectId=${row.courseSubjectId}&cycleNumber=${cycleNumber}${group}`)
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Box>
                        <H3>{formatMessage({ id: 'salaryTitle' })}</H3>
                        <Box sx={{ color: ui.sub, fontSize: '13.5px', marginTop: '6px', maxWidth: 640 }}>
                            {formatMessage({ id: 'salarySubtitle' })}
                        </Box>
                    </Box>
                    {!!subjectTitle && <Chip
                        label={`${formatMessage({ id: isClosed ? 'salaryStatusClosed' : 'salaryStatusOpen' })}`}
                        sx={{
                            fontWeight: 700,
                            backgroundColor: isClosed ? ui.track : tone.ok.bg,
                            color: isClosed ? ui.sub : tone.ok.text,
                        }}
                    />}
                </Header>

                <KpiRow>
                    <Kpi label={formatMessage({ id: 'salaryMentors' })} value={`${mentorCount}`} />
                    <Kpi label={formatMessage({ id: 'salaryTotalPayable' })} value={money(totalPayable)} dark />
                    <Kpi label={formatMessage({ id: 'salaryAvgSalary' })} value={money(averageSalary)} />
                    <Kpi
                        label={formatMessage({ id: 'salaryFillStatus' })}
                        value={`${filledCount} / ${mentorCount}`}
                    />
                </KpiRow>

                <Toolbar>
                    <Select
                        size="small"
                        value={cycleNumber}
                        onChange={(e) => setCycleNumber(Number(e.target.value))}
                        sx={selectSx}
                    >
                        {MONTHS.map((m) => (
                            <MenuItem key={m} value={m}>
                                {m} {formatMessage({ id: 'month' })}
                            </MenuItem>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        displayEmpty
                        value={subjectTitle}
                        onChange={(e) => { setSubjectTitle(e.target.value); setGroupId(0) }}
                        sx={selectSx}
                    >
                        <MenuItem value="">{formatMessage({ id: 'salaryAllSubjects' })}</MenuItem>
                        {subjects.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        displayEmpty
                        value={effectiveGroupId}
                        onChange={(e) => setGroupId(Number(e.target.value))}
                        sx={selectSx}
                    >
                        <MenuItem value={0}>{formatMessage({ id: 'allGroups' })}</MenuItem>
                        {groups.map((g) => (
                            <MenuItem key={g.id} value={g.id}>{g.title}</MenuItem>
                        ))}
                    </Select>

                    <TextField
                        size="small"
                        placeholder={formatMessage({ id: 'salarySearchMentor' })}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                </Toolbar>

                {isFetching ? (
                    <LinearProgress />
                ) : (
                    <TableContainer sx={tableStyles.container}>
                        <Table>
                            <TableHead sx={tableStyles.head}>
                                <TableRow>
                                    <TableCell>{formatMessage({ id: 'colMentor' })}</TableCell>
                                    <TableCell>{formatMessage({ id: 'colSubject' })}</TableCell>
                                    <TableCell>{formatMessage({ id: 'group' })}</TableCell>
                                    <TableCell align="center">{formatMessage({ id: 'colStudents' })}</TableCell>
                                    <TableCell align="right">{formatMessage({ id: 'colMonthTotal' })}</TableCell>
                                    <TableCell align="center">{formatMessage({ id: 'colStatus' })}</TableCell>
                                    <TableCell>{formatMessage({ id: 'approvalCol' })}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows.map((m) => (
                                    <TableRow
                                        key={`${m.mentorUserId}:${m.courseSubjectId}:${m.groupId ?? 0}`}
                                        hover
                                        sx={{ ...tableStyles.row, cursor: 'pointer' }}
                                        onClick={() => openDetail(m)}
                                    >
                                        <TableCell sx={{ fontWeight: 600 }}>{mentorName(m)}</TableCell>
                                        <TableCell>{m.subjectTitleKz || m.subjectTitleRu}</TableCell>
                                        <TableCell sx={{ color: ui.sub }}>{m.groupTitle || '-'}</TableCell>
                                        <TableCell align="center">{m.studentCount}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: ui.pink }}>
                                            {money(m.total)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {m.closed ? (
                                                <Chip
                                                    size="small"
                                                    label={formatMessage({ id: 'salaryStatusClosed' })}
                                                    sx={{ fontWeight: 700, fontSize: '11px', backgroundColor: ui.track, color: ui.sub }}
                                                />
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label={formatMessage({ id: m.surveyFilled ? 'salaryFilled' : 'salaryFilling' })}
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '11px',
                                                        backgroundColor: m.surveyFilled ? tone.ok.bg : tone.warn.bg,
                                                        color: m.surveyFilled ? tone.ok.text : tone.warn.text,
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <ApprovalBadge status={m.approvalStatus} comment={m.approvalComment} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {visibleRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ color: ui.muted, padding: '32px' }}>
                                            {formatMessage({ id: 'salaryNoMentors' })}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box sx={{ marginTop: '28px' }}>
                    <H3>{formatMessage({ id: 'salaryRules' })}</H3>
                    <Box sx={{ marginTop: '14px' }}>
                        <SalaryFactorRules config={config} />
                    </Box>
                </Box>
            </Content>
        </Wrapper>
    )
}

const Kpi: React.FC<{ label: string; value: string; dark?: boolean }> = ({ label, value, dark }) => (
    <Box
        sx={{
            border: `1px solid ${dark ? ui.pinkSoft : ui.border}`,
            backgroundColor: dark ? ui.pinkSoft : ui.card,
            borderRadius: '14px',
            padding: '16px 18px',
        }}
    >
        <Box sx={{ fontSize: '12px', color: dark ? ui.pinkDark : ui.sub, marginBottom: '8px' }}>{label}</Box>
        <Box sx={{ fontSize: '22px', fontWeight: 700, color: dark ? ui.pinkDark : ui.ink }}>{value}</Box>
    </Box>
)

const selectSx = {
    minWidth: 150,
    borderRadius: ui.radius,
    fontSize: '13.5px',
    fontWeight: 600,
    backgroundColor: ui.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
}

export default MentorSalaryPage
